import { EditOtherDistributionModal } from 'components/EditOtherDistributionModal';
import type { BimWorkOrderChangeOtherPriceData } from 'gql/graphql';
import { ProjectContractTypeEnum, OtherCostCalculationTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { tabItemsDistribution } from 'pages/Project/Common/types';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useBimWorkOrderChangeDistributionOther } from './hooks';

type TOrderChangeEditDistributionModalProps = {
    dataRow: BimWorkOrderChangeOtherPriceData | undefined;
    onCloseModal: () => void;
    refreshTable: () => void;
    constructionTypeId?: number;
};

export const BimWorkOrderChangeEditDistribution = ({
    dataRow,
    onCloseModal,
    refreshTable,
    constructionTypeId,
}: TOrderChangeEditDistributionModalProps) => {
    const { id: projectId, orderChangeId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const isFrame = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );
    const workOrderChangeId = Number(orderChangeId);

    const {
        control,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        reset,
        watch,
        formState: { isDirty, errors },
    } = useForm({ defaultValues: {} });
    const [activeTab, setActiveTab] = useState(tabItemsDistribution[0]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseModal,
        });

    const onSuccess = useCallback(() => {
        reset();
        onCloseModal();
        refreshTable();
    }, [onCloseModal, refreshTable, reset]);

    const { distributionData, isDataFetching, isUpdateFetching, resetDataDialog, updateOtherDistributionHandler } =
        useBimWorkOrderChangeDistributionOther({
            workOrderOtherPriceId: dataRow?.workOrderOtherPriceId,
            workOrderChangeOtherPriceId: dataRow?.workOrderChangeOtherPriceId,
            constructionTypeId,
            onSuccess,
            workOrderChangeId,
        });

    const handleCloseModal = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onCloseModal();
            resetDataDialog();
        }
    }, [handleOpenConfirmationDialog, isDirty, onCloseModal, resetDataDialog]);

    const isCalculating = useMemo(
        () => distributionData?.contractOtherCost?.calculatingType === OtherCostCalculationTypeEnum.Calculation,
        [distributionData?.contractOtherCost?.calculatingType],
    );

    const workOrderChangeCompositeIds = useMemo(
        () => `${workOrderChangeId}_${dataRow?.workOrderOtherPriceId}_${dataRow?.workOrderChangeOtherPriceId}`,
        [dataRow?.workOrderChangeOtherPriceId, dataRow?.workOrderOtherPriceId, workOrderChangeId],
    );

    return (
        <EditOtherDistributionModal
            handleCancel={handleCloseModal}
            onSubmit={updateOtherDistributionHandler}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            // @ts-ignore
            bimWorkOrderOtherDistributionData={distributionData}
            isDataFetching={isDataFetching}
            currentConstructionTypeId={constructionTypeId}
            isOpenWarningDialog={isOpenConfirmationDialog}
            handleSubmitWarningDialog={handleActionQuery}
            handleCancelWarningDialog={handleCloseConfirmationDialog}
            formControl={control}
            formHandleSubmit={handleSubmit}
            setValue={setValue}
            isLoading={isUpdateFetching}
            watch={watch}
            contractOtherCostId={dataRow?.contractOtherCostId}
            isCalculating={isCalculating}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            isWorkOrderChange
            workOrderChangeCompositeIds={workOrderChangeCompositeIds}
            isFrameContract={isFrame}
        />
    );
};

BimWorkOrderChangeEditDistribution.displayName = 'BimWorkOrderChangeEditDistribution';
